import { Navigate, Outlet, useLocation } from 'react-router';

import { useIsAuthenticated, useAuthUser, useIsPlatformAdmin } from './useAuth';

interface ProtectedRouteProps {
  /** If provided, the user must have this role to access the route. */
  requiredRole?: string;
  /**
   * Si es true, solo pasa el Admin del tenant de plataforma (PymeCode).
   * Espeja [PlatformAdminOnly] del backend. El rol Admin por si solo no alcanza:
   * un Admin de cualquier tenant lo cumpliria.
   */
  requirePlatformAdmin?: boolean;
  /** Where to redirect when not authenticated. Defaults to /login. */
  redirectTo?: string;
  /** Where to redirect when authenticated but role is insufficient. Defaults to /dashboard. */
  unauthorizedRedirectTo?: string;
}

/**
 * Smart route guard component.
 *
 * - If not authenticated → redirects to `redirectTo` (default: /login), preserving
 *   the attempted location in state so LoginPage can redirect back after login.
 * - If authenticated and mustChangePassword === true → redirects to /first-setup,
 *   blocking access to any other protected route until the setup is complete.
 * - If authenticated but `requiredRole` is set and the user's role does not match
 *   → redirects to `unauthorizedRedirectTo` (default: /dashboard).
 * - Otherwise → renders the child routes via <Outlet />.
 */
export function ProtectedRoute({
  requiredRole,
  requirePlatformAdmin = false,
  redirectTo = '/login',
  unauthorizedRedirectTo = '/dashboard',
}: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const user = useAuthUser();
  const isPlatformAdmin = useIsPlatformAdmin();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // First-setup gate: authenticated users who haven't completed setup must do so
  // before they can access any other route in the app.
  if (user?.mustChangePassword === true) {
    return <Navigate to="/first-setup" replace />;
  }

  if (requiredRole !== undefined && user?.role !== requiredRole) {
    return <Navigate to={unauthorizedRedirectTo} replace />;
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    return <Navigate to={unauthorizedRedirectTo} replace />;
  }

  return <Outlet />;
}
